import React, { useState } from 'react'
import map from 'lodash/map'
import includes from 'lodash/includes'
import { Badge } from 'react-bootstrap'
import type { BlogCategoryNode } from '../../BlogCategory/blogCategoryTree.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { DragHandle, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { getBlogCategoryMoveExcludedIds } from '../../BlogCategory/blogCategoryTree.utils'
import { NO_EDIT_PERMISSION_REASON } from '../../PermissionReasons'
import { useBlogCategoryTreeViewContext } from './Context'
import { BlogCategoryNameModal } from './BlogCategoryNameModal'
import { BlogCategoryRowMenu } from './BlogCategoryRowMenu'
import { DragSourceKindEnum } from './useBlogCategoryDragDrop'
import styles from './BlogCategoryTreeView.module.scss'

export type BlogCategoryTreeNodeProps = {
  category: BlogCategoryNode
}

export const BlogCategoryTreeNode = ({ category }: BlogCategoryTreeNodeProps) => {
  const {
    flat,
    canEdit,
    selectedBlogCategory,
    expandedIds,
    countBlogsIn,
    hasChildren,
    selectBlogCategory,
    toggleExpanded,
    renameBlogCategory,
    getDragSourceProps,
    getDropTargetProps,
  } = useBlogCategoryTreeViewContext()
  const selected = selectedBlogCategory?._id === category._id
  const expandable = hasChildren(category._id)
  const expanded = includes(expandedIds, category._id)
  const count = countBlogsIn(category._id)
  const excludedBlogCategoryIds = getBlogCategoryMoveExcludedIds(flat, category._id)
  const [renaming, setRenaming] = useState(false)

  return (
    <li>
      <HorizontalStack
        gap={1}
        className={`${styles.row} rounded px-1 py-1`}
        data-selected={selected}
        {...getDropTargetProps(category._id)}
      >
        <DragHandle
          title={canEdit ? 'Drag to move this category' : NO_EDIT_PERMISSION_REASON}
          aria-label={`Drag ${category.name}`}
          disabled={!canEdit}
          hideTooltipOnDrag
          {...getDragSourceProps({ kind: DragSourceKindEnum.BlogCategory, _id: category._id, excludedBlogCategoryIds })}
        />
        {expandable ? (
          <BusyButton
            variant="link"
            bsSize="sm"
            className={`${styles.toggle} flex-shrink-0 p-0`}
            icon={expanded ? 'chevron-down' : 'chevron-right'}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${category.name}`}
            aria-expanded={expanded}
            onClick={() => toggleExpanded(category._id)}
          />
        ) : (
          <span className={`${styles.toggle} flex-shrink-0`} />
        )}
        <BusyButton
          variant="link"
          className="flex-grow-1 text-start text-truncate p-0 text-decoration-none"
          text={category.name}
          onClick={() => selectBlogCategory(category._id)}
        />
        <Badge bg="secondary-subtle" text="secondary-emphasis" pill>
          {count}
          <span className="visually-hidden"> {count === 1 ? 'blog' : 'blogs'}</span>
        </Badge>
        <BlogCategoryRowMenu
          category={category}
          excludedBlogCategoryIds={excludedBlogCategoryIds}
          onRename={() => setRenaming(true)}
        />
        <BlogCategoryNameModal
          show={renaming}
          title={`Rename ${category.name}`}
          initialName={category.name}
          onSave={(newName) => renameBlogCategory(category._id, newName)}
          onClose={() => setRenaming(false)}
        />
      </HorizontalStack>
      <ShowIf condition={expandable && expanded}>
        <ul className="list-unstyled m-0 ps-4">
          {map(category.items, (child) => (
            <BlogCategoryTreeNode key={child._id} category={child} />
          ))}
        </ul>
      </ShowIf>
    </li>
  )
}
